/**
 * Draft Session Service
 *
 * Manages Firestore CRUD for the draftSessions collection.
 * Path: schools/{schoolId}/draftSessions/{sessionId}
 *
 * A draft session document holds:
 *   - type: string (e.g. 'timetableAssignments')
 *   - context: object (e.g. { timetableId, classId })
 *   - status: 'active' | 'committed' | 'discarded'
 *   - entries: map of entryId -> entry data
 *   - agentProgress: { status, currentStep, stepsCompleted, stepsTotal }
 *   - createdAt, updatedAt, createdBy
 */

import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  onSnapshot,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { db, auth } from '../firebase';

class DraftSessionService {
  getDocRef(schoolId, sessionId) {
    return doc(db, 'schools', schoolId, 'draftSessions', sessionId);
  }

  /**
   * Create a new draft session
   * Returns the sessionId
   */
  async create(schoolId, { type, context }) {
    const sessionId = `draft_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const docRef = this.getDocRef(schoolId, sessionId);

    await setDoc(docRef, {
      type,
      context: context || {},
      status: 'active',
      entries: {},
      agentProgress: {
        status: 'idle',
        currentStep: '',
        stepsCompleted: 0,
        stepsTotal: 0,
      },
      createdBy: auth.currentUser?.uid || 'unknown',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return sessionId;
  }

  /**
   * Write a single entry to the draft session
   */
  async writeEntry(schoolId, sessionId, entryId, data) {
    const docRef = this.getDocRef(schoolId, sessionId);
    await updateDoc(docRef, {
      [`entries.${entryId}`]: { ...data, _updatedAt: Date.now() },
      updatedAt: serverTimestamp(),
    });
  }

  /**
   * Write multiple entries at once
   */
  async writeEntries(schoolId, sessionId, entriesMap) {
    const docRef = this.getDocRef(schoolId, sessionId);
    const updates = { updatedAt: serverTimestamp() };
    Object.entries(entriesMap).forEach(([entryId, data]) => {
      updates[`entries.${entryId}`] = { ...data, _updatedAt: Date.now() };
    });
    await updateDoc(docRef, updates);
  }

  /**
   * Remove an entry from the draft session
   */
  async removeEntry(schoolId, sessionId, entryId) {
    const docRef = this.getDocRef(schoolId, sessionId);
    await updateDoc(docRef, {
      [`entries.${entryId}`]: deleteField(),
      updatedAt: serverTimestamp(),
    });
  }

  /**
   * Update agent progress display
   */
  async updateProgress(schoolId, sessionId, progress) {
    const docRef = this.getDocRef(schoolId, sessionId);
    const updates = { updatedAt: serverTimestamp() };
    Object.entries(progress).forEach(([key, value]) => {
      updates[`agentProgress.${key}`] = value;
    });
    await updateDoc(docRef, updates);
  }

  /**
   * Subscribe to real-time updates on a draft session
   * Returns unsubscribe function
   */
  subscribe(schoolId, sessionId, callback) {
    const docRef = this.getDocRef(schoolId, sessionId);
    return onSnapshot(docRef, (snapshot) => {
      if (snapshot.exists()) {
        callback({ id: snapshot.id, ...snapshot.data() });
      } else {
        callback(null);
      }
    }, (error) => {
      console.error('Draft session subscription error:', error);
      callback(null);
    });
  }

  /**
   * Mark session as committed and return all entries for batch-writing
   */
  async commit(schoolId, sessionId) {
    const docRef = this.getDocRef(schoolId, sessionId);
    const snapshot = await getDoc(docRef);

    if (!snapshot.exists()) {
      throw new Error('Draft session not found');
    }

    const data = snapshot.data();
    if (data.status !== 'active') {
      throw new Error(`Cannot commit session with status: ${data.status}`);
    }

    // Mark as committed
    await updateDoc(docRef, {
      status: 'committed',
      committedAt: serverTimestamp(),
      committedBy: auth.currentUser?.uid || 'unknown',
      updatedAt: serverTimestamp(),
    });

    return data.entries || {};
  }

  /**
   * Mark session as discarded
   */
  async discard(schoolId, sessionId) {
    const docRef = this.getDocRef(schoolId, sessionId);
    await updateDoc(docRef, {
      status: 'discarded',
      discardedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  /**
   * Get a draft session document
   */
  async get(schoolId, sessionId) {
    const docRef = this.getDocRef(schoolId, sessionId);
    const snapshot = await getDoc(docRef);
    if (!snapshot.exists()) return null;
    return { id: snapshot.id, ...snapshot.data() };
  }
}

export const draftSessionService = new DraftSessionService();
export default draftSessionService;
